import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Plane, Ship, Truck, User, Warehouse, FileCheck, Building2, Package, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/AuthContext';
import LanguageSelector from '@/components/LanguageSelector';
import NotificationBell from '@/components/NotificationBell';

const LOGO_URL = "/assets/elementary-cargo-logo-globe.png";

const Header = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const servicesTimeoutRef = useRef(null);
  const solutionsTimeoutRef = useRef(null);
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  
  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (servicesTimeoutRef.current) clearTimeout(servicesTimeoutRef.current);
      if (solutionsTimeoutRef.current) clearTimeout(solutionsTimeoutRef.current);
    };
  }, []);
  
  // Handlers for hover-based dropdown
  const handleServicesMouseEnter = () => {
    if (servicesTimeoutRef.current) clearTimeout(servicesTimeoutRef.current);
    setIsServicesOpen(true);
  };
  
  const handleServicesMouseLeave = () => {
    servicesTimeoutRef.current = setTimeout(() => {
      setIsServicesOpen(false);
    }, 150);
  };

  const handleSolutionsMouseEnter = () => {
    if (solutionsTimeoutRef.current) clearTimeout(solutionsTimeoutRef.current);
    setIsSolutionsOpen(true);
  };
  
  const handleSolutionsMouseLeave = () => {
    solutionsTimeoutRef.current = setTimeout(() => {
      setIsSolutionsOpen(false);
    }, 150);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setIsSolutionsOpen(false);
  }, [location]);

  const servicesSubmenu = [
    { name: t('nav.airFreight'), href: '/services/air-freight', icon: Plane },
    { name: t('nav.oceanFreight'), href: '/services/ocean-freight', icon: Ship },
    { name: t('nav.roadFreight'), href: '/services/road-freight', icon: Truck },
    { name: t('nav.warehousing'), href: '/services/warehousing', icon: Warehouse },
    { name: t('nav.customs'), href: '/services/customs', icon: FileCheck },
  ];

  const solutionsSubmenu = [
    { name: 'How It Works', href: '/how-it-works', icon: Package },
    { name: 'Track', href: '/tracking', icon: MapPin },
    { name: 'Sectors', href: '/sectors', icon: Building2 },
    { name: 'FAQ', href: '/faq', icon: FileCheck },
    { name: 'Routes', href: '/routes', icon: Ship },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-navy-900/98 backdrop-blur-xl shadow-lg py-2'
          : 'bg-navy-900/90 backdrop-blur-md py-4'
      }`}
      data-testid="main-header"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between">
          {/* Logo - Left Aligned */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group flex-shrink-0" 
            data-testid="logo-link"
          >
            <motion.img
              src={LOGO_URL}
              alt="Elementary Cargo"
              className={`object-contain transition-all duration-500 ${
                isScrolled ? 'h-10' : 'h-12'
              }`}
              whileHover={{ scale: 1.05 }}
            />
            <div className="flex flex-col">
              <div className="flex items-baseline gap-1">
                <span className={`text-white font-semibold tracking-wider transition-all duration-500 ${
                  isScrolled ? 'text-base' : 'text-lg'
                }`}>
                  ELEMENTARY
                </span>
                <span className={`text-gold font-medium tracking-wider transition-all duration-500 ${
                  isScrolled ? 'text-base' : 'text-lg'
                }`}>
                  CARGO
                </span>
              </div>
              <span className={`text-steel-400 tracking-widest uppercase transition-all duration-500 ${
                isScrolled ? 'text-[8px]' : 'text-[10px]'
              }`}>
                Moving with Purpose
              </span>
            </div>
          </Link>

          {/* Center Navigation - Desktop */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            <Link
              to="/"
              className={`text-sm font-medium tracking-wider uppercase transition-colors ${
                isActive('/') ? 'text-gold' : 'text-white/90 hover:text-gold'
              }`}
              data-testid="nav-home"
            >
              {t('nav.home')}
            </Link>

            <Link
              to="/about"
              className={`text-sm font-medium tracking-wider uppercase transition-colors ${
                isActive('/about') ? 'text-gold' : 'text-white/90 hover:text-gold'
              }`}
              data-testid="nav-about"
            >
              {t('nav.about')}
            </Link>

            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleServicesMouseEnter}
              onMouseLeave={handleServicesMouseLeave}
            >
              <button
                className={`flex items-center gap-1 text-sm font-medium tracking-wider uppercase transition-colors ${
                  location.pathname.startsWith('/services') ? 'text-gold' : 'text-white/90 hover:text-gold'
                }`}
                data-testid="nav-services"
                aria-expanded={isServicesOpen}
                aria-haspopup="true"
                aria-label={t('nav.services')}
              >
                {t('nav.services')}
                <ChevronDown className={`w-3 h-3 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              
              <AnimatePresence>
                {isServicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 mt-2 bg-navy-900/95 backdrop-blur-md border border-navy-700 rounded-lg min-w-[200px] p-2 shadow-xl z-50"
                    data-testid="services-dropdown"
                  >
                    {servicesSubmenu.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="flex items-center gap-3 px-3 py-2 text-white/90 hover:text-gold hover:bg-white/5 rounded transition-colors text-sm"
                        onClick={() => setIsServicesOpen(false)}
                      >
                        <item.icon className="w-4 h-4 text-gold/70" />
                        {item.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Industry Solutions Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleSolutionsMouseEnter}
              onMouseLeave={handleSolutionsMouseLeave}
            >
              <button
                className={`flex items-center gap-1 text-sm font-medium tracking-wider uppercase transition-colors ${
                  location.pathname === '/sectors' || location.pathname === '/faq' || location.pathname === '/routes'
                    ? 'text-gold' : 'text-white/90 hover:text-gold'
                }`}
                data-testid="nav-solutions"
                aria-expanded={isSolutionsOpen}
                aria-haspopup="true"
                aria-label={t('nav.solutions') || 'Solutions'}
              >
                {t('nav.solutions') || 'Solutions'}
                <ChevronDown className={`w-3 h-3 transition-transform ${isSolutionsOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              
              <AnimatePresence>
                {isSolutionsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 mt-2 bg-navy-900/95 backdrop-blur-md border border-navy-700 rounded-lg min-w-[180px] p-2 shadow-xl z-50"
                    data-testid="solutions-dropdown"
                  >
                    {solutionsSubmenu.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="flex items-center gap-3 px-3 py-2 text-white/90 hover:text-gold hover:bg-white/5 rounded transition-colors text-sm"
                        onClick={() => setIsSolutionsOpen(false)}
                      >
                        <item.icon className="w-4 h-4 text-gold/70" />
                        {item.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/contact"
              className={`text-sm font-medium tracking-wider uppercase transition-colors ${
                isActive('/contact') ? 'text-gold' : 'text-white/90 hover:text-gold'
              }`}
              data-testid="nav-contact"
            >
              {t('nav.contact')}
            </Link>
          </nav>

          {/* Right Section - Language, Notifications, Auth */}
          <div className="hidden lg:flex items-center gap-4">
            <LanguageSelector />
            <NotificationBell />
            
            {/* Auth Section */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white hover:text-gold hover:bg-white/10 gap-2 h-9"
                    data-testid="user-menu-btn"
                    aria-label="User menu"
                  >
                    <User className="w-4 h-4" aria-hidden="true" />
                    <span className="text-sm">{user?.first_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-navy-900/95 backdrop-blur-md border-navy-700 min-w-[180px]">
                  {user?.role === 'admin' && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="text-white/90 hover:text-gold hover:bg-white/5 cursor-pointer text-sm">
                          Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/employee-portal" className="text-white/90 hover:text-gold hover:bg-white/5 cursor-pointer text-sm">
                          Employee Portal
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  {user?.role === 'employee' && (
                    <DropdownMenuItem asChild>
                      <Link to="/employee-portal" className="text-white/90 hover:text-gold hover:bg-white/5 cursor-pointer text-sm">
                        Employee Portal
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'client' && (
                    <DropdownMenuItem asChild>
                      <Link to="/client-portal" className="text-white/90 hover:text-gold hover:bg-white/5 cursor-pointer text-sm">
                        My Portal
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={logout} className="text-white/90 hover:text-gold hover:bg-white/5 cursor-pointer text-sm">
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="login-link" aria-label={t('nav.login')}>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-white border-gold/50 hover:bg-gold hover:text-navy-900 gap-2 h-9"
                  data-testid="login-btn"
                >
                  <User className="w-4 h-4" aria-hidden="true" />
                  {t('nav.login')}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Right Actions */}
          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSelector />
            {!isAuthenticated && (
              <Link to="/login" data-testid="mobile-login-link" aria-label={t('nav.login')}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gold hover:text-gold/80 hover:bg-white/10 h-8 px-2"
                  aria-label={t('nav.login')}
                >
                  <User className="w-4 h-4" aria-hidden="true" />
                </Button>
              </Link>
            )}
            {isAuthenticated && <NotificationBell />}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-white hover:text-gold transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-navy-900/98 backdrop-blur-lg border-t border-white/10"
          >
            <div className="container-custom py-6">
              <nav className="flex flex-col gap-1">
                <Link
                  to="/"
                  className={`px-4 py-3 text-base font-medium rounded-lg uppercase tracking-wide ${
                    isActive('/') ? 'text-gold bg-white/5' : 'text-white/80 hover:text-gold hover:bg-white/5'
                  }`}
                >
                  {t('nav.home')}
                </Link>
                
                <Link
                  to="/about"
                  className={`px-4 py-3 text-base font-medium rounded-lg uppercase tracking-wide ${
                    isActive('/about') ? 'text-gold bg-white/5' : 'text-white/80 hover:text-gold hover:bg-white/5'
                  }`}
                >
                  {t('nav.about')}
                </Link>

                {/* Services Section */}
                <div className="py-2">
                  <span className="text-gold text-sm font-medium tracking-wide px-4 uppercase">{t('nav.services')}</span>
                  <div className="mt-2 ml-4 flex flex-col gap-1">
                    {servicesSubmenu.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="flex items-center gap-3 px-4 py-3 text-white/80 hover:text-gold hover:bg-white/5 rounded-lg transition-all"
                      >
                        <item.icon className="w-4 h-4 text-gold/70" />
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Solutions Section */}
                <div className="py-2">
                  <span className="text-gold text-sm font-medium tracking-wide px-4 uppercase">{t('nav.solutions') || 'Solutions'}</span>
                  <div className="mt-2 ml-4 flex flex-col gap-1">
                    {solutionsSubmenu.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="flex items-center gap-3 px-4 py-3 text-white/80 hover:text-gold hover:bg-white/5 rounded-lg transition-all"
                      >
                        <item.icon className="w-4 h-4 text-gold/70" />
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>

                <Link
                  to="/contact"
                  className={`px-4 py-3 text-base font-medium rounded-lg uppercase tracking-wide ${
                    isActive('/contact') ? 'text-gold bg-white/5' : 'text-white/80 hover:text-gold hover:bg-white/5'
                  }`}
                >
                  {t('nav.contact')}
                </Link>
              </nav>
              
              {!isAuthenticated && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <Link to="/login">
                    <Button className="w-full bg-gold text-navy-900 hover:bg-gold/90 font-semibold uppercase tracking-wide">
                      {t('nav.login')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
